import { useNewCode } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { getTimezoneName } from '@calendly/timezone-tools';
import { Avatar } from '@calendly/ui/components/avatar';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { FullStoryExclude, FullStoryMask } from '@calendly/ui/components/fullstory-privacy';
import { IconButton } from '@calendly/ui/components/icon-button';
import { MobilePhoneIcon, TimezoneIcon, StarIcon, StarFullIcon, CloseIcon, MailSendEnvelopeIcon } from '@calendly/ui/components/icons';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorAlert } from '@calendly/ui/theme';
import classNames from 'classnames';
import { useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ScheduleWithAContactProvider } from 'features/contacts/schedule_with_a_contact/schedule-with-a-contact-provider';
import { currentContactProfileSelector, isContactDrawerOpenSelector, activeContactDrawerSelector, isShareAvailabilityFeatureEnabledSelector, getContactDrawerSource } from 'features/contacts/selectors';
import { openSendPopupForContact, INVITEE_EXPERIENCE_SEND_FROM_CALENDLY_Q3 } from 'features/send_from_calendly';
import { useTrack } from 'hooks/analytics_events';
import { getInitials } from 'shared/utils';
import { trackShareAvailabilityEvent, trackScheduleInRealTimeEvent, trackFlowStartedEvent } from './analytics';
import { SCHEDULE_WITH_CONTACT_PROVIDER } from './constants';
import { MeetingHistorySection } from './profile_meeting_sections/meeting_history_section';
import { CreateEventButton } from './shared/create_event_button';
import { ShareAvailabilityButton } from './shared/share_availability_button';
import { toggleContactDrawerOpen, setCurrentContact } from './slice';
import { getActualContactAttributesView } from './utils';
const containerIsOpenCss = "c1s9c0dk";
const containerCss = "chfw90v";
const Container = forwardRef((props, ref) => {
  const {
    isOpen,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerIsOpenCss]: isOpen
  }, props.className)} />;
});
const profileContainerAiAssistantCss = "p142k6l";
const profileContainerIsOpenCss = "p15epopd";
const profileContainerIsPhoneCss = "p1njbsp5";
const profileContainerCss = "pbxqldi";
const ProfileContainer = forwardRef((props, ref) => {
  const {
    aiAssistant,
    isOpen
  } = props;
  const isPhone = useDeviceType('isPhone');
  return <FixedContainer ref={ref} {...props} className={classNames(profileContainerCss, {
    [profileContainerAiAssistantCss]: aiAssistant,
    [profileContainerIsOpenCss]: isOpen,
    [profileContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const closeButtonCss = "c10egma0";
const CloseButton = forwardRef((props, ref) => <IconButton ref={ref} {...props} className={classNames(closeButtonCss, props.className)} />);
const avatarContainerCss = "axdd2b4";
const AvatarContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(avatarContainerCss, props.className)} />;
});
const iconButtonsContainerCss = "ivcvpw4";
const IconButtonsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(iconButtonsContainerCss, props.className)} />;
});
const scheduleButtonsContainerIsPhoneCss = "s1ap5vs0";
const scheduleButtonsContainerCss = "sfh2i04";
const ScheduleButtonsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(scheduleButtonsContainerCss, {
    [scheduleButtonsContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const dividerIsPhoneCss = "d1obhlx8";
const dividerCss = "d1f9i0e0";
const Divider = forwardRef((props, ref) => {
  const {
    isPhone,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'hr';
  return <Component ref={ref} {...restProps} className={classNames(dividerCss, {
    [dividerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const headerContainerCss = "h1yvrxgr";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, props.className)} />;
});
const sectionHeaderCss = "s1l4au2c";
const SectionHeader = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(sectionHeaderCss, props.className)} />;
});
const detailsCss = "dm3i2z4";
const Details = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(detailsCss, props.className)} />;
});
const contactTextWrapperCss = "cxz0bi7";
const ContactTextWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(contactTextWrapperCss, props.className)} />;
});
export const ContactProfile = () => {
  const dispatch = useDispatch();
  const currentContact = useSelector(currentContactProfileSelector);
  const isContactDrawerOpen = useSelector(isContactDrawerOpenSelector);
  const activeContactDrawer = useSelector(activeContactDrawerSelector);
  const openedByAIAssistant = useSelector(getContactDrawerSource);
  const [isShareStarted, setIsShareStarted] = useState(false);
  const [sharedContact, setIsSharedContact] = useState(null);
  const showForDesktop = useDeviceType('isDesktopUp');
  const track = useTrack();
  const isShareAvailabilityFeatureEnabled = useSelector(isShareAvailabilityFeatureEnabledSelector);
  const isButtonsExperimentEnabled = useNewCode('org_expansion_share_cta_feature')?.enabled;
  const isSendFromCalendlyEnabled = useNewCode(INVITEE_EXPERIENCE_SEND_FROM_CALENDLY_Q3)?.enabled;
  const shouldOpenProfile = currentContact !== null && isContactDrawerOpen && activeContactDrawer === 'profile';
  const contact = getActualContactAttributesView(currentContact);
  const onClose = () => {
    dispatch(toggleContactDrawerOpen());
    dispatch(setCurrentContact(null));
  };
  if (!shouldOpenProfile) return null;
  return <Container isOpen={shouldOpenProfile}>
      <ProfileContainer isOpen={shouldOpenProfile} id="contact-profile" aiAssistant={openedByAIAssistant}>
        {contact && <>
            <CloseButton size="small" id="contact_profile_close_button" aria-label={I18n.t('publisher.action.close')} onClick={onClose}>
              <CloseIcon />
            </CloseButton>
            <HeaderContainer>
              <AvatarContainer>
                <FullStoryExclude>
                  <Avatar size="big" letter={getInitials(contact.name)} />
                </FullStoryExclude>
                <IconButtonsContainer>
                  <IconButton size="small" aria-label="Favorite-Profile" disabled>
                    {contact.favorite ? <StarFullIcon data-testid="star-full-icon" color={colorAlert} /> : <StarIcon data-testid="star-empty-icon" />}
                  </IconButton>
                </IconButtonsContainer>
              </AvatarContainer>
              <TitleText level={3} as="h2" size="medium">
                <FullStoryMask>
                  {contact.name}
                </FullStoryMask>
              </TitleText>
              <BodyText size="medium" color="muted">
                {I18n.t('publisher.contacts.profile.contact')}
              </BodyText>

              <ScheduleButtonsContainer>
                {showForDesktop && <CreateEventButton onClick={() => {
              setIsSharedContact(contact);
              setIsShareStarted(true);
              trackScheduleInRealTimeEvent({
                contactUuid: contact.uuid,
                source: 'contact_profile',
                step: 'open_modal',
                track
              });
              trackFlowStartedEvent({
                flowLocation: 'contact_profile',
                track
              });
            }} isButtonExperimentEnabled={isButtonsExperimentEnabled} />}
                {isShareAvailabilityFeatureEnabled && isSendFromCalendlyEnabled && <ShareAvailabilityButton onClick={() => {
              dispatch(openSendPopupForContact({
                contact,
                source: SCHEDULE_WITH_CONTACT_PROVIDER.PARENT_COMPONENT.CONTACT_PROFILE
              }));
              trackShareAvailabilityEvent({
                contactUuid: contact.uuid,
                source: 'contact_profile',
                step: 'open_modal',
                track
              });
            }} isButtonExperimentEnabled={isButtonsExperimentEnabled} />}
              </ScheduleButtonsContainer>
            </HeaderContainer>
            <Divider />
            <SectionHeader>
              <BodyText size="medium" color="muted" weight="bold">
                {I18n.t('publisher.contacts.profile.details')}
              </BodyText>
            </SectionHeader>
            <Details>
              <ContactTextWrapper>
                <MailSendEnvelopeIcon spacingRight="8" />
                <BodyText size="medium">{contact.email}</BodyText>
              </ContactTextWrapper>
              {contact.timeZone && <ContactTextWrapper>
                  <TimezoneIcon spacingRight="8" />
                  <BodyText size="medium">{getTimezoneName(contact.timeZone)}</BodyText>
                </ContactTextWrapper>}
              {contact.phone && <ContactTextWrapper>
                  <MobilePhoneIcon spacingRight="8" />
                  <BodyText size="medium">{contact.phone}</BodyText>
                </ContactTextWrapper>}
            </Details>
            <MeetingHistorySection />
          </>}
      </ProfileContainer>
      <ScheduleWithAContactProvider isFlowStarted={isShareStarted} contact={sharedContact} onFlowFinish={() => {
      setIsShareStarted(false);
      setIsSharedContact(null);
    }} parentSource={SCHEDULE_WITH_CONTACT_PROVIDER.PARENT_COMPONENT.CONTACT_PROFILE} />
    </Container>;
};

require("./contact_profile.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./contact_profile.js");